import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Intent } from '@blueprintjs/core';

import { Breadcrumbs } from 'components/navigation';
import { ActiveStatus } from 'components/status';
import { ArrayToEllipsesString } from 'components/formatting';
import { DateToLocal } from 'components/dates';
import { LinkButton } from 'components/buttons';
import { Filter, FilterButton } from 'components/filtering';
import { FormTextInput } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import { NotificationToaster } from 'components/notifications';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';

import { useDebounce } from 'hooks/useDebounce';
import { SystemMessagesService, OptionsService } from 'services';

const requiredAction = "Internal";
const filterName = "systemMessages";
const activeFilterKey = "active";
const messageTypeFilterKey = "systemMessageTypeId";
const filterColumns = 2;
const defaultPageSize = 25;
const debounceTimeout = 750;
const messageLength = 100;
const loadErrorMessage = "Could not load the system messages. Please try again.";
const messageTypesErrorMessage = "Could not load the message types. Please refresh the page.";

const tableHeaders = ["Status", "Message", "Start Date", "End Date", "Message Type"];

const endDateSortableHeader = { headerName: "End Date", sortName: "ENDDATE" };
const descendingSortDirection = "D";

const sortableHeaders = [
    { headerName: "Status", sortName: "ACTIVE" },
    { headerName: "Message", sortName: "MESSAGE" },
    { headerName: "Start Date", sortName: "STARTDATE" },
    endDateSortableHeader,
    { headerName: "Message Type", sortName: "SYSTEMMESSAGETYPENAME" }
];

// The filter panel is multi-select, so no selection means every message - there is no "all" item.
const activeFilterItems = [
    { id: "true", name: "Active" },
    { id: "false", name: "Inactive" }
];

const defaultArray = [];

export function SystemMessageListing({ breadcrumbs = defaultArray }) {

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    // The filter panel reports its selection - which is persisted between visits - before the
    // message types resolve, so holding the first search until setup completes means it already
    // carries any applied filters rather than firing unfiltered and then again with them.
    const [initialSetupComplete, setInitialSetupComplete] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [searchTerm, setSearchTerm] = useState("");
    // The listing opens on the messages that ended most recently, rather than on the order the server
    // falls back to.
    const [sortedBy, setSortedBy] = useState(endDateSortableHeader);
    const [sortedDir, setSortedDir] = useState(descendingSortDirection);
    const [appliedFilters, setAppliedFilters] = useState(defaultArray);
    const [showFilters, setShowFilters] = useState(false);
    const [messageTypeFilterItems, setMessageTypeFilterItems] = useState(defaultArray);

    // The filter panel stays interactive while a search is in flight, so two searches can overlap and
    // resolve out of order. Only the most recent request may write to the table.
    const latestRequestId = useRef(0);

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const canAccessPage = UserHasAccess(requiredAction);
    const isFiltered = debouncedSearchTerm !== "" || appliedFilters.length > 0;

    const pageFilters = [
        { name: activeFilterKey, displayName: "Status", items: activeFilterItems },
        { name: messageTypeFilterKey, displayName: "Message Type", items: messageTypeFilterItems }
    ];

    let noDataHeading = "No system messages";
    let noDataMessage = "There are no system messages, please click 'Add new system message' to get started.";

    if (isFiltered) {
        noDataHeading = "No matching system messages";
        noDataMessage = "No system messages match your search and filters, please try different criteria.";
    }

    useEffect(loadMessageTypes, []);
    useEffect(fetchData, [debouncedSearchTerm, pageNumber, pageSize, sortedBy, sortedDir, appliedFilters, initialSetupComplete]);

    function loadMessageTypes() {
        if (!canAccessPage) {
            return;
        }

        OptionsService.getSystemMessageTypes().then((messageTypes) => {
            setMessageTypeFilterItems((messageTypes ?? []).map((messageType) => {
                return { id: `${messageType.systemMessageTypeId}`, name: messageType.systemMessageTypeName };
            }));
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, messageTypesErrorMessage);
        }).finally(() => {
            setInitialSetupComplete(true);
        });
    }

    function mapFilters() {
        return appliedFilters.map((appliedFilter) => {
            return { key: appliedFilter.name, value: appliedFilter.value };
        });
    }

    function buildRows(messages) {
        return messages.map((message) => {
            return [
                <ActiveStatus key={`status-${message.systemMessageID}`} isActive={message.active} />,
                <Link key={`message-${message.systemMessageID}`} to={`/internal-admin/system-messages/${message.systemMessageID}`}>
                    <ArrayToEllipsesString length={messageLength}>{message.message}</ArrayToEllipsesString>
                </Link>,
                <DateToLocal key={`start-date-${message.systemMessageID}`} format="DD/MM/YYYY HH:mm">{message.startDate}</DateToLocal>,
                <DateToLocal key={`end-date-${message.systemMessageID}`} format="DD/MM/YYYY HH:mm">{message.endDate}</DateToLocal>,
                <span key={`message-type-${message.systemMessageID}`}>{message.systemMessageTypeName}</span>
            ];
        });
    }

    function fetchData() {
        if (!canAccessPage || !initialSetupComplete) {
            return;
        }

        setLoadingData(true);

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: mapFilters()
        };

        const requestId = latestRequestId.current + 1;
        latestRequestId.current = requestId;

        SystemMessagesService.searchSystemMessages(searchRequest).then((response) => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            setTableRows(buildRows(response?.data ?? []));
            setTotalRecords(response?.totalCount ?? 0);
        }, () => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            NotificationToaster.show(Intent.DANGER, loadErrorMessage);
        }).finally(() => {
            if (requestId !== latestRequestId.current) {
                return;
            }

            setLoadingData(false);
        });
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onToggleFilter() {
        setShowFilters((previousState) => !previousState);
    }

    function onFilterChange(selectedFilters) {
        setAppliedFilters(selectedFilters);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <h1>System Message Management</h1>
                    <div className="intro-text">
                        <p>From here you can create and manage system messages.</p>
                    </div>
                    <div className="spacer-bottom">
                        <LinkButton intent="primary" text="Add new system message" href="/internal-admin/system-messages/create" id="new-system-message" />
                    </div>

                    <div className="filter-controls spacer-bottom">
                        <div className="inline-items">
                            <div className="inline-item fill">
                                <div className="form-field-full">
                                    <FormTextInput
                                        id="system-messages-search-field"
                                        placeholder="Search system messages"
                                        value={searchTerm}
                                        onChange={onSearchChange}
                                        disabled={loadingData}
                                        large
                                        icon="search"
                                    />
                                </div>
                            </div>
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>
                        </div>
                        <Filter
                            filterName={filterName}
                            filters={pageFilters}
                            visible={showFilters}
                            numOfColumns={filterColumns}
                            onUpdate={onFilterChange}
                            onClickOutside={onToggleFilter}
                        />
                    </div>

                    <ListingTable
                        id="listing-table-system-messages"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loadingData}
                        noDataHeading={noDataHeading}
                        noDataMessage={noDataMessage}
                        onPagingChange={onPagingChange}
                        startPage={pageNumber}
                        pageSize={pageSize}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onSort}
                    />
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

SystemMessageListing.propTypes = {
    breadcrumbs: PropTypes.array
};
